/**
 * Analytics Dialog
 * 
 * A two-step dialog for:
 * - Step 1: Define life domains + Select a calendar (clicking a calendar navigates to step 2)
 * - Step 2: Map calendar colors to life domains
 */

import { useState, useEffect, useCallback } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Loader } from '@/components/ai-elements/loader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { ArrowLeft, LifeDomainsIcon, ChevronRight, Tag } from '@/components/icons'
import { useLifeDomains } from '@/context/life-domains-context'
import { useCalendars, type CalendarResource, type CalendarIntegration } from '@/context/calendars-context'
import { useAuth } from '@/context/auth-context'
import { CalendarList } from '@/components/automation-rules/calendar-list'
import { LifeDomainsDialog } from './life-domains-dialog'
import { CalendarIntegrationsDialog } from '@/components/integrations/calendar-integrations-dialog'
import { GOOGLE_CALENDAR_COLORS, type GoogleCalendarColorId } from '@/components/automation-rules/types'
import { SectionHeader } from '@/components/ui/section-header'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'

// Color mapping row type
interface ColorMapping {
  colorId: GoogleCalendarColorId
  isIgnored: boolean
  lifeDomainId: string | null
  labelId: string | null
}

interface AnalyticsDialogProps {
  open: boolean
  onClose: () => void
  /** Optional callback when analytics is successfully saved (for onboarding) */
  onSuccess?: () => void
}

export function AnalyticsDialog({ open, onClose, onSuccess }: AnalyticsDialogProps) {
  const { session } = useAuth()
  const { lifeDomains, loading: lifeDomainsLoading, getLabelsForDomain } = useLifeDomains()
  const { integrations, loading: calendarsLoading, refreshCalendars } = useCalendars()
  
  // Dialog view state
  const [view, setView] = useState<'main' | 'color-mapping'>('main')
  
  // Selected calendar state (composite key: integration_id:calendar_id)
  const [selectedCalendarKey, setSelectedCalendarKey] = useState<string | null>(null)
  
  // Life domains dialog state
  const [isLifeDomainsDialogOpen, setIsLifeDomainsDialogOpen] = useState(false)
  
  // Calendar integrations dialog state (for empty state CTA)
  const [isCalendarIntegrationsDialogOpen, setIsCalendarIntegrationsDialogOpen] = useState(false)
  
  // Color mappings state for the selected calendar
  const [colorMappings, setColorMappings] = useState<ColorMapping[]>([])
  const [loadingMappings, setLoadingMappings] = useState(false)
  const [savingMappings, setSavingMappings] = useState(false)
  const [deletingMappings, setDeletingMappings] = useState(false)
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  
  // Track if mappings exist in DB (for showing Delete vs showing nothing)
  const [hasExistingMappings, setHasExistingMappings] = useState(false)
  
  // All day events default setting (stored as minutes in DB: 0, 15, 30, 60, 1440)
  const [allDayEventsDefault, setAllDayEventsDefault] = useState<string | null>(null)
  
  // Planning only mode: calendar used for planning analytics, not actual results
  const [planningOnly, setPlanningOnly] = useState(false)
  
  // Get the selected calendar info
  const getSelectedCalendarInfo = useCallback((): { 
    integration: CalendarIntegration | undefined
    calendar: CalendarResource | undefined
    calendarName: string
  } => {
    if (!selectedCalendarKey) {
      return { integration: undefined, calendar: undefined, calendarName: '' }
    }
    
    const [integrationId, calendarId] = selectedCalendarKey.split(':')
    const integration = integrations.find(i => i.id === integrationId)
    const calendar = integration?.calendars.find(c => c.id === calendarId)
    
    return {
      integration,
      calendar,
      calendarName: calendar?.summary || 'Selected Calendar'
    }
  }, [selectedCalendarKey, integrations])
  
  // Initialize default color mappings (all 11 colors)
  const initializeDefaultMappings = useCallback((): ColorMapping[] => {
    const colorIds = Object.keys(GOOGLE_CALENDAR_COLORS) as GoogleCalendarColorId[]
    return colorIds.map(colorId => ({
      colorId,
      isIgnored: false,
      lifeDomainId: null,
      labelId: null,
    }))
  }, [])
  
  // Convert DB minutes value to UI string
  const minutesToUiValue = (minutes: number | null): string | null => {
    if (minutes === null) return null
    const map: Record<number, string> = {
      0: 'ignore',
      15: '15min',
      30: '30min',
      60: '1h',
      1440: '24h'
    }
    return map[minutes] ?? null
  }
  
  // Convert UI string to DB minutes value
  const uiValueToMinutes = (value: string): number => {
    const map: Record<string, number> = {
      'ignore': 0,
      '15min': 15,
      '30min': 30,
      '1h': 60,
      '24h': 1440
    }
    return map[value] ?? 0
  }
  
  // Fetch existing color mappings for the selected calendar
  const fetchColorMappings = useCallback(async () => {
    if (!selectedCalendarKey) return
    
    const { calendar } = getSelectedCalendarInfo()
    if (!calendar?.integrationCalendarId) {
      // Calendar not in integrations_calendars yet, use default mappings
      setColorMappings(initializeDefaultMappings())
      setHasExistingMappings(false)
      setAllDayEventsDefault(null)
      setPlanningOnly(false)
      return
    }
    
    setLoadingMappings(true)
    try {
      // Fetch color mappings
      const { data, error } = await supabase
        .from('analytics_calendars_mapping')
        .select('*')
        .eq('integrations_calendar_id', calendar.integrationCalendarId)
      
      // Fetch all_day_events_default and planning_only from integrations_calendars
      const { data: calendarData, error: calendarError } = await supabase
        .from('integrations_calendars')
        .select('all_day_events_default, planning_only')
        .eq('id', calendar.integrationCalendarId)
        .single()
      
      if (error) {
        console.error('Error fetching color mappings:', error)
        toast.error('Failed to load color mappings')
        setColorMappings(initializeDefaultMappings())
        setHasExistingMappings(false)
        setAllDayEventsDefault(null)
        setPlanningOnly(false)
        return
      }
      
      // Set all day events default from DB (convert minutes to UI value)
      // Use != null to catch both null and undefined
      if (!calendarError && calendarData?.all_day_events_default != null) {
        setAllDayEventsDefault(minutesToUiValue(calendarData.all_day_events_default))
      } else {
        setAllDayEventsDefault(null)
      }
      if (!calendarError && calendarData?.planning_only != null) {
        setPlanningOnly(calendarData.planning_only === true)
      } else {
        setPlanningOnly(false)
      }
      
      if (!data || data.length === 0) {
        // No mappings exist yet, use defaults
        setColorMappings(initializeDefaultMappings())
        setHasExistingMappings(false)
        return
      }
      
      // Convert DB rows to our state format
      const colorIds = Object.keys(GOOGLE_CALENDAR_COLORS) as GoogleCalendarColorId[]
      const domainIds = new Set(lifeDomains.map(d => d.id))
      const mappings = colorIds.map(colorId => {
        const existingMapping = data.find(row => row.color_id === colorId)
        let lifeDomainId = existingMapping?.life_domain_id ?? null
        if (lifeDomainId && !domainIds.has(lifeDomainId)) {
          lifeDomainId = null
        }
        let labelId = existingMapping?.label_id ?? null
        if (labelId && lifeDomainId) {
          const domainLabels = getLabelsForDomain(lifeDomainId)
          if (!domainLabels.some(l => l.id === labelId)) {
            labelId = null
          }
        } else if (!lifeDomainId) {
          labelId = null
        }
        return {
          colorId,
          isIgnored: existingMapping?.is_ignored ?? false,
          lifeDomainId,
          labelId,
        }
      })
      
      setColorMappings(mappings)
      setHasExistingMappings(true)
    } catch (error) {
      console.error('Error fetching color mappings:', error)
      toast.error('Failed to load color mappings')
      setColorMappings(initializeDefaultMappings())
      setHasExistingMappings(false)
      setAllDayEventsDefault(null)
      setPlanningOnly(false)
    } finally {
      setLoadingMappings(false)
    }
  }, [selectedCalendarKey, getSelectedCalendarInfo, initializeDefaultMappings, getLabelsForDomain, lifeDomains])
  
  // Load mappings when entering color mapping view
  useEffect(() => {
    if (view === 'color-mapping' && selectedCalendarKey) {
      fetchColorMappings()
    }
  }, [view, selectedCalendarKey, fetchColorMappings])
  
  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setView('main')
      setSelectedCalendarKey(null)
      setColorMappings([])
      setHasExistingMappings(false)
      setShowDeleteConfirm(false)
      setAllDayEventsDefault(null)
      setPlanningOnly(false)
    }
  }, [open])
  
  // Handle calendar selection - automatically navigate to color mapping view
  const handleCalendarChange = (compositeKey: string | null) => {
    if (compositeKey) {
      setSelectedCalendarKey(compositeKey)
      setView('color-mapping')
    } else {
      setSelectedCalendarKey(null)
    }
  }
  
  // Handle mapping change for a color
  const handleMappingChange = (colorId: GoogleCalendarColorId, field: 'isIgnored' | 'lifeDomainId' | 'labelId', value: boolean | string | null) => {
    setColorMappings(prev => prev.map((mapping): ColorMapping => {
      if (mapping.colorId !== colorId) return mapping
      
      if (field === 'isIgnored') {
        return {
          ...mapping,
          isIgnored: value as boolean,
          lifeDomainId: (value as boolean) ? null : mapping.lifeDomainId,
          labelId: (value as boolean) ? null : mapping.labelId,
        }
      }
      
      if (field === 'lifeDomainId') {
        // When changing life domain, reset label selection
        return {
          ...mapping,
          lifeDomainId: value as string | null,
          labelId: null,
        }
      }

      return {
        ...mapping,
        labelId: value as string | null,
      }
    }))
  }
  
  // Save color mappings
  const handleSaveMappings = async () => {
    if (!selectedCalendarKey || !session?.access_token) return
    
    // Validate all day events default is selected
    if (!allDayEventsDefault) {
      toast.error('Please select how to handle all-day events')
      return
    }
    
    const { integration, calendar } = getSelectedCalendarInfo()
    if (!integration || !calendar) return
    
    setSavingMappings(true)
    try {
      // Call edge function to save mappings and activate analytics service
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calendar-color-mappings-save`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            integrationId: integration.id,
            calendarIdExternal: calendar.id,
            allDayEventsDefault: uiValueToMinutes(allDayEventsDefault),
            planningOnly: planningOnly,
            mappings: colorMappings.map(m => ({
              colorId: m.colorId,
              isIgnored: m.isIgnored,
              lifeDomainId: m.lifeDomainId,
              labelId: m.labelId,
            }))
          })
        }
      )
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || errorData.message || 'Failed to save mappings')
      }
      
      const result = await response.json()
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to save mappings')
      }
      
      setHasExistingMappings(true)
      toast.success('Color mappings saved successfully')
      
      // Refresh calendars context to reflect the new analyticsIsActive status
      await refreshCalendars()
      
      // Call onSuccess callback for onboarding
      onSuccess?.()
      
      // Go back to main view
      setView('main')
      setSelectedCalendarKey(null)
    } catch (error) {
      console.error('Error saving color mappings:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to save color mappings')
    } finally {
      setSavingMappings(false)
    }
  }
  
  // Delete all color mappings for the calendar
  const handleDeleteMappings = async () => {
    if (!selectedCalendarKey || !session?.access_token) return
    
    const { calendar } = getSelectedCalendarInfo()
    if (!calendar?.integrationCalendarId) return
    
    setDeletingMappings(true)
    try {
      // Call edge function to delete mappings and deactivate analytics service
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calendar-color-mappings-delete`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            integrationsCalendarId: calendar.integrationCalendarId
          })
        }
      )
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || errorData.message || 'Failed to delete mappings')
      }
      
      const result = await response.json()
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete mappings')
      }
      
      toast.success('Color mappings deleted')
      setShowDeleteConfirm(false)
      
      // Refresh calendars context to reflect the analyticsIsActive status change
      await refreshCalendars()
      
      // Go back to main view
      setView('main')
      setSelectedCalendarKey(null)
    } catch (error) {
      console.error('Error deleting color mappings:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to delete color mappings')
    } finally {
      setDeletingMappings(false)
    }
  }
  
  // Go back to main view
  const handleBackToMain = () => {
    setView('main')
    setColorMappings([])
    setHasExistingMappings(false)
    setShowDeleteConfirm(false)
  }
  
  // Close dialog
  const handleCloseDialog = () => {
    setView('main')
    setSelectedCalendarKey(null)
    onClose()
  }

  const loading = lifeDomainsLoading || calendarsLoading

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCloseDialog()}>
        <DialogContent 
          className="
            max-w-none
            p-0
            border-0 shadow-none
            w-full h-[80vh] max-h-[80vh]
            md:w-[800px] md:h-[70vh] md:max-h-[70vh]
            flex flex-col
            fixed !top-auto inset-x-0 !bottom-0 !left-0 !translate-x-0 !translate-y-0
            md:!left-1/2 md:!top-1/2 md:!bottom-auto md:inset-x-auto md:!-translate-x-1/2 md:!-translate-y-1/2
            rounded-t-3xl md:rounded-lg
          "
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          hideClose
        >
          <DialogDescription className="sr-only">
            {view === 'main' 
              ? 'Configure life domains and assign calendars for analytics tracking.' 
              : 'Map calendar event colors to your life domains.'}
          </DialogDescription>
          
          <DialogHeader className="px-2 pt-6 pb-4 flex-shrink-0">
            <div className="flex items-center justify-between gap-4">
              {/* Back Button */}
              <Button
                variant="secondary"
                onClick={view === 'main' ? handleCloseDialog : handleBackToMain}
                className="flex items-center gap-2 md:px-4 px-2 md:rounded-md rounded-full"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden md:inline">Back</span>
              </Button>
              
              <DialogTitle className="flex-1 text-center">
                {view === 'main' 
                  ? 'Analytics' 
                  : 'Analytics configuration'}
              </DialogTitle>
              
              {/* Spacer to keep title centered */}
              <div className="w-[40px] md:w-[72px]"></div>
            </div>
          </DialogHeader>
          
          {/* Main View - Step 1 */}
          {view === 'main' && (
            <div className="px-4 pb-4 overflow-y-auto flex-1 min-h-0 flex flex-col">
              {loading ? (
                <div className="flex-1 flex items-center justify-center">
                  <Loader size={32} />
                </div>
              ) : (
                <div className="space-y-8 flex-1">
                  {/* Card 1: Define Life Domains */}
                  <div className="space-y-2">
                    <SectionHeader 
                      title="Life domains and Labels" 
                      description="Manage your Life domains, Labels and their properties."
                    />
                    <Card 
                      className="cursor-pointer hover:bg-accent/50 transition-colors py-0"
                      onClick={() => setIsLifeDomainsDialogOpen(true)}
                    >
                      <CardHeader className="py-4 px-1 md:px-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-md bg-transparent">
                              <LifeDomainsIcon className="h-6 w-6 text-card-foreground" />
                            </div>
                            <div>
                              <CardTitle className="text-base">Life domains and Labels setup</CardTitle>
                            </div>
                          </div>
                          <ChevronRight className="h-6 w-6 text-muted-foreground" />
                        </div>
                      </CardHeader>
                    </Card>
                  </div>
                  
                  {/* Card 2: Assign Calendars */}
                  <div className="space-y-2 flex-1 flex flex-col min-h-0">
                    <SectionHeader 
                      title="Calendars"
                      description="Select a calendar to configure and enable it for analytics"
                    />
                    <div className="flex-1 min-h-0 overflow-y-auto">
                      <CalendarList
                        selectedCalendarId={selectedCalendarKey}
                        onChange={handleCalendarChange}
                        isSelectable={true}
                        onConnectClick={() => setIsCalendarIntegrationsDialogOpen(true)}
                      />
                    </div>
                  </div>
                </div>
              )}
              
            </div>
          )}
          
          {/* Color Mapping View - Step 2 */}
          {view === 'color-mapping' && (
            <>
              {/* Content: Scrollable area with General settings + Color mapping */}
              <div className="px-4 overflow-y-auto flex-1 min-h-0 flex flex-col">
                {loadingMappings ? (
                  <div className="flex-1 flex items-center justify-center">
                    <Loader size={32} />
                  </div>
                ) : (
                  <>
                    {/* General Settings Card */}
                    <div className="space-y-2 mb-4">
                      <SectionHeader title="General settings" />
                      <Card>
                        <CardContent className="p-4 space-y-4">
                          {/* Calendar name */}
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Calendar</span>
                            <span className="text-sm font-medium">{getSelectedCalendarInfo().calendarName}</span>
                          </div>
                          
                          {/* All day events dropdown */}
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">All day events</span>
                            <Select 
                              value={allDayEventsDefault ?? ''} 
                              onValueChange={setAllDayEventsDefault}
                            >
                              <SelectTrigger className="w-[120px]">
                                <SelectValue placeholder="Select..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="ignore">Ignore</SelectItem>
                                <SelectItem value="15min">15min</SelectItem>
                                <SelectItem value="30min">30min</SelectItem>
                                <SelectItem value="1h">1h</SelectItem>
                                <SelectItem value="24h">24h</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {/* Planning only switch */}
                          <div className="flex flex-col gap-1.5">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium">Planning only</span>
                              <Switch
                                checked={planningOnly}
                                onCheckedChange={setPlanningOnly}
                              />
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Calendar events will only be considered for planning analytics and not actual results.
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                    
                    {/* Color mapping section */}
                    <div className="space-y-2">
                      <SectionHeader title="Color mapping" />
                      <Card>
                        <CardContent className="p-0">
                          <div className="divide-y divide-border">
                            {colorMappings.map((mapping) => {
                              const colorInfo = GOOGLE_CALENDAR_COLORS[mapping.colorId]
                              const displayHex = mapping.colorId === '0'
                                ? (getSelectedCalendarInfo().calendar?.backgroundColor ?? colorInfo.hex)
                                : colorInfo.hex
                              return (
                                <div 
                                  key={mapping.colorId}
                                  className="flex flex-wrap items-center gap-4 p-3"
                                >
                                  {/* Color circle */}
                                  <div 
                                    className="w-6 h-6 rounded-full flex-shrink-0 border border-border"
                                    style={{ backgroundColor: displayHex }}
                                    title={colorInfo.name}
                                  />
                                  
                                  {/* Ignore switch */}
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground">Ignore</span>
                                    <Switch
                                      checked={mapping.isIgnored}
                                      onCheckedChange={(checked) => 
                                        handleMappingChange(mapping.colorId, 'isIgnored', checked)
                                      }
                                    />
                                  </div>
                                  
                                  {/* Life domain + Label dropdowns: stacked on mobile, side-by-side on larger screens */}
                                  <div className="flex-1 min-w-0 flex flex-col md:flex-row md:items-start gap-2">
                                    <div className="w-full md:min-w-[140px] md:max-w-[220px]">
                                      <Select
                                        value={mapping.lifeDomainId || 'unassigned'}
                                        onValueChange={(value) => 
                                          handleMappingChange(
                                            mapping.colorId, 
                                            'lifeDomainId', 
                                            value === 'unassigned' ? null : value
                                          )
                                        }
                                        disabled={mapping.isIgnored}
                                      >
                                        <SelectTrigger 
                                          className={cn(
                                            "w-full",
                                            mapping.isIgnored && "opacity-50"
                                          )}
                                        >
                                          <SelectValue placeholder="Select life domain" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="unassigned">
                                            <span className="text-muted-foreground">Unassigned</span>
                                          </SelectItem>
                                          {lifeDomains.map((domain) => (
                                            <SelectItem key={domain.id} value={domain.id}>
                                              <div className="flex items-center gap-2">
                                                <LifeDomainsIcon
                                                  className="h-3.5 w-3.5 flex-shrink-0"
                                                  style={{ color: domain.color || '#3b82f6' }}
                                                />
                                                {domain.title}
                                              </div>
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>

                                    {/* Label dropdown (shown when domain selected; shows Default when domain has no labels) */}
                                    {mapping.lifeDomainId && (() => {
                                      const domainLabels = getLabelsForDomain(mapping.lifeDomainId)
                                      const options: Array<{ id: string; name: string; color: string; is_default: boolean }> =
                                        domainLabels.length >= 1
                                          ? domainLabels
                                          : [{ id: 'default', name: 'Default', color: 'var(--muted-foreground)', is_default: true }]
                                      return (
                                        <div className="w-full md:w-36 shrink-0">
                                          <Select
                                            value={mapping.labelId || 'default'}
                                            onValueChange={(value) =>
                                              handleMappingChange(
                                                mapping.colorId,
                                                'labelId',
                                                value === 'default' ? null : value
                                              )
                                            }
                                            disabled={mapping.isIgnored}
                                          >
                                            <SelectTrigger
                                              className={cn(
                                                "w-full",
                                                mapping.isIgnored && "opacity-50"
                                              )}
                                            >
                                              <SelectValue placeholder="Label" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {options.map((label) => (
                                                <SelectItem key={label.id} value={label.is_default ? 'default' : label.id}>
                                                  <div className="flex items-center gap-2">
                                                    <Tag
                                                      className="h-3.5 w-3.5 flex-shrink-0"
                                                      style={{ color: label.color }}
                                                    />
                                                    {label.name}
                                                  </div>
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </div>
                                    )
                                    })()}
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </>
                )}
              </div>
              
              {/* Footer: Fixed buttons outside scrollable content */}
              {!loadingMappings && (
                <div className="px-4 pb-4 pt-4 flex-shrink-0 flex gap-3">
                  {hasExistingMappings && (
                    <Button
                      variant="outline"
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={savingMappings}
                      className="flex-1 border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
                    >
                      Delete
                    </Button>
                  )}
                  <Button
                    onClick={handleSaveMappings}
                    disabled={savingMappings}
                    className="flex-1"
                  >
                    {savingMappings ? (
                      <>
                        <Loader size={16} className="mr-2" />
                        Saving...
                      </>
                    ) : (
                      hasExistingMappings ? 'Update' : 'Enable'
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Life Domains Dialog (nested) */}
      <LifeDomainsDialog
        open={isLifeDomainsDialogOpen}
        onClose={() => setIsLifeDomainsDialogOpen(false)}
      />
      
      {/* Calendar Integrations Dialog (for empty state CTA) */}
      <CalendarIntegrationsDialog
        open={isCalendarIntegrationsDialogOpen}
        onClose={() => setIsCalendarIntegrationsDialogOpen(false)}
      />
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md" hideClose>
          <DialogHeader>
            <DialogTitle>Delete Color Mappings</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete all color mappings for this calendar? 
              This will also disable analytics tracking for this calendar.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deletingMappings}
              className="w-32"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteMappings}
              disabled={deletingMappings}
              className="w-32"
            >
              {deletingMappings ? (
                <>
                  <Loader size={16} className="mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}




